import { useState, useRef, useEffect } from "react"
import PrimaryButton from "../../utilities/helperComponents/PrimaryButton"
import FilterSelector from "./FilterSelector"
import OutlineButton from "../../utilities/helperComponents/OutlineButton"
import { useSelector } from "react-redux"
import { leadFilterTypes } from "./leadFilterOptions"
import UnderlineButton from "../../utilities/helperComponents/UnderlineButton"
import SelectedListFilterInfo from "./SelectedListFilterInfo"
import SelectedRangeFilterInfo from "./SelectedRangeFilterInfo"
import { useDispatch } from "react-redux"
import { captureCurrentFilters, removeAllFilters, resetPredefinedFilter, setTableUpdate, toggleJumpIntoQueue, updateAllFilterExclusion, updatePendingFilters } from "../../features/leadTableFiltersSlice"
import Dropdown from "../../utilities/helperComponents/Dropdown"
import Icon from "../../assets/icons/icon"
import { filterLeadsPayload, getLeadsByFilterOptions } from "../../utilities/leadFunctions"
import { setBackup, setQueue, setWholeQueue } from "../../features/queueSlice"
import '../../css/checkbox.css'
import "../../css/filterableLeadTable.css";
import { fetchWithDefaults } from "../../config/fetchDefault"
import { fetchLeadDataThunk } from "../../features/leadDataInfoSlice"
import Warning from "../warning"
import { SuccessAlert } from "../successAlert"
import { setLoadingGif } from "../../features/loadingSlice"
import SelectedPredefinedFilterInfo from "./SelectedPredefinedFilterInfo"
import SaveCurrentLeadTableFilter from "./SaveCurrentLeadTableFilter"
import image from '../../assets/icons/loading-loader.gif'
import moment from "moment/moment"

const SortOrder = {
	None: 0,
	Asc: 1,
	Desc: 2,
}
/**
 * @typedef {Object} TableColumn
 * @property {string} label
 * @property {Array<string>} dbFields
 */

/** @type {Array<TableColumn>} */
const tableColumns = [
	{
		label: "Name",
		dbFields: [
			"borrower_last",
			"borrower_first",
		],
	},
	{
		label: "State",
		dbFields: [
			"state",
		],
	},
	{
		label: "Contacted",
		dbFields: [
			"last_contacted",
		],
	},
	{
		label: "Assigned",
		dbFields: [
			"date_assigned",
		],
	},
	{
		label: "Calls",
		dbFields: [
			"amount_contacted",
		],
	},
];

function LeadTable({ expandSession, setRefreshQueue, jumpIntoQueue, textingNumbers, setLeadCard }) {
	const queue=useSelector((state) => state.queue);
    const dispatch = useDispatch()

    const excludeAllRef = useRef(null)

    /**
     * @type {number}
     */
    const activeFilterCount = useSelector((state) => state.leadTableFilter.activeFilterCount)
    const agentInfo = useSelector((state) => state.agent.userData)

    /**
     * @type {leadFilterOptions}
     */
    const { date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status, predefined_filter, jump_into_queue, save_filters, pending_filters, update_table } = useSelector((state) => state.leadTableFilter)

    const [showFilterList, setShowFilterList] = useState(false)
    const [selectedFilters, setSelectedFilters] = useState([])
    const [selectedLeadIds, setSelectedLeadIds] = useState([])
    const [leads, setLeads] = useState([])
    const [maxPage, setMaxPage] = useState(Number.POSITIVE_INFINITY)
    const [totalCount, setTotalCount] = useState(Number.POSITIVE_INFINITY)
    const [showBulkActionModal, setShowBulkActionModal] = useState(false)
    const [modalActions, setModalActions] = useState({
        label: '',
        search: '',
        actions: []
    })
    const [errorMessage, setErrorMessage] = useState(false);
    const [warning, setWarning] = useState(false);
    const [successTrigger, setSuccessTrigger] = useState(false)
	const [excludeAll, setExcludeAll] = useState(false);

    useEffect(() => {
        const filterTitles = {
            'state': 'State',
            'tags': 'Lead Tags',
            'lead_type': 'Lead Type',
            'lead_level': 'Lead Level',
            'lead_status': 'Lead Status',
            'date_assigned': 'Date Assigned',
            'borrower_age': 'Lead Age',
            'last_contacted': 'Last Contacted',
			'never_contacted': 'Never Contacted',
            'amount_contacted': 'Total Contacted',
        }

        const filterNames = {
            'state': 'state',
            'tags': 'lead_tags',
            'lead_type': 'lead_type',
            'lead_level': 'lead_level',
            'lead_status': 'lead_status',
            'date_assigned': 'date_assigned',
            'borrower_age': 'lead_age',
            'last_contacted': 'last_contacted',
			'never_contacted': 'never_contacted',
            'amount_contacted': 'amount_contacted',
        }

        const allActiveFilters = [date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status].filter(f => f.active)

        const filters = allActiveFilters
            .map(f => {
                return {
                    filter: f,
                    type: Array.isArray(f?.list) ? leadFilterTypes.LIST : leadFilterTypes.RANGE,
                    title: filterTitles[f.field],
                    filterName: filterNames[f.field]
                }
            })

        const allExcluded = allActiveFilters.every(f => f.excluded === true)
        const allUnExcluded = allActiveFilters.every(f => f.excluded === false)

        if (allExcluded === true && excludeAllRef.current?.checked === false) {
            excludeAllRef.current.checked = true
        }

        if ((allUnExcluded === true || allExcluded === false) && excludeAllRef.current?.checked) {
            excludeAllRef.current.checked = false
        }

        // console.log({ allExcluded, allUnExcluded, excludeAllRef, allActiveFilters })
        setSelectedFilters(filters)
    }, [date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status])

    const [tableOptions, setTableOptions] = useState({
        start: 0,
        end: 50,
        size: 50,
        row: 1,
	    sortIndex: -1,
	    sortOrder: SortOrder.None,
        // maxRow: 100
    })

    const pageOptions = [
        {
            label: '10',
            value: 10
        },
        {
            label: '25',
            value: 25
        },
        {
            label: '50',
            value: 50
        },
        {
            label: '100',
            value: 100
        },
    ]

    const updateTableSize = (size) => {
        const updatedOptions = { ...tableOptions, size }

        updatedOptions.start = 0
        updatedOptions.end = updatedOptions.size
        updatedOptions.row = 1

        setTableOptions(updatedOptions)
    }

    const getNextRow = () => {
        if (tableOptions.end >= totalCount) {
            // console.log('getNextRow-skipped', { tableOptions, totalCount })
            return
        }

        const updatedOptions = { ...tableOptions }

        let newStart = updatedOptions.end
        let newEnd = updatedOptions.end + updatedOptions.size

        updatedOptions.row = Math.min(updatedOptions.row + 1, maxPage)

        updatedOptions.start = newStart
        updatedOptions.end = Math.min(newEnd, totalCount)

        // console.log('getNextRow', { updatedOptions, tableOptions })
        setTableOptions(updatedOptions)
    }

    const getPrevRow = () => {
        if (tableOptions.start === 0) {
            return
        }
        const updatedOptions = { ...tableOptions }

        let newStart = updatedOptions.start - updatedOptions.size
        let newEnd = updatedOptions.start

        updatedOptions.row = Math.max(updatedOptions.row - 1, 1)

        updatedOptions.start = Math.max(newStart, 0)
        updatedOptions.end = Math.max(newEnd, updatedOptions.size)
        // console.log('getPrevRow', { updatedOptions, tableOptions })
        setTableOptions(updatedOptions)
    }

    const getLeads = async () => {
		/** @type {Array<SortOption>} */
		let sortOptions = [];
	    if ((tableOptions.sortIndex >= 0) && (tableOptions.sortIndex < tableColumns.length)) {
		    const tblCol = tableColumns[tableOptions.sortIndex];
		    const sortDir = tableOptions.sortOrder === SortOrder.Desc
			    ? "DESC"
			    : "ASC";
		    for (const dbField of tblCol.dbFields) {
			    sortOptions.push({
				    field: dbField,
				    direction: sortDir,
			    });
		    }
	    } else {
		    console.error("getLeads: Got invalid column index: %s", tableOptions.sortIndex);
		    // Allow continuation of routine which will use a default sort.
	    }
        const filterData = filterLeadsPayload(date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status, predefined_filter, tableOptions.row, tableOptions.size, sortOptions);
        setLoading(true)

        try {
            const { filterListOptions, filterRangeOptions, paginationOptions, sortOptions } = filterData
            const leadResponse = await getLeadsByFilterOptions(filterListOptions, filterRangeOptions, paginationOptions, sortOptions, agentInfo.agentId, predefined_filter)

            updateLeadData(leadResponse.data ?? [], leadResponse.meta)
            dispatch(updatePendingFilters(false))
            if(update_table){
                dispatch(setTableUpdate(false))
            }
        } catch (error) {
            console.error()
        }
        setLoading(false)
    }

    const getInitialLeads = async () => {
        const filterData = filterLeadsPayload(date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status, predefined_filter, tableOptions.row, tableOptions.size)

        const paginationOptions = {
            page: 1,
            numPerPage: tableOptions.size
        }

        setLoading(true)

        try {
            const { filterListOptions, filterRangeOptions, sortOptions } = filterData
            const leadResponse = await getLeadsByFilterOptions(filterListOptions, filterRangeOptions, paginationOptions, sortOptions, agentInfo.agentId, predefined_filter)

            getIntiial(leadResponse.data ?? [], leadResponse.meta)
            dispatch(updatePendingFilters(false))

            if(update_table){
                dispatch(setTableUpdate(false))
            }
        } catch (error) {
            console.error(error)
        }

        setLoading(false)
    }

    useEffect(() => {
        getLeads()
    }, [tableOptions, predefined_filter, update_table === true])

    const updateSelectedleads = (leadId, addLead) => {
        let updatedLeads
        switch (addLead) {
            case true:
                updatedLeads = [...new Set([...selectedLeadIds, leadId])]
                break;

            case false:
                updatedLeads = selectedLeadIds.filter(id => id !== leadId)
                break;
        }

        setSelectedLeadIds(updatedLeads)
    }

    const dailSelected = () => {
        if (selectedLeadIds.length > 0) {
			let jump=true;
			if(queue.queue.length > 0){
				jump=false;
			}
            const newQueue = leads.filter(l => selectedLeadIds.includes(l.lead_id))
            dispatch(setQueue(newQueue));
            setRefreshQueue(newQueue[0])
			if(jump){
				jumpIntoQueue();
			}
			else{
				expandSession();
			}
            setSelectedLeadIds([]);
        }
    }

    const bulk = async (id, type) => {

        const list = selectedLeadIds;
        let numbers = '';
        let numberWarning = false;
        //this object doesn't have to look like this, but the backend is created to allow multple bulk actions at one time so it needs to have this structure
        const action = [{ [type]: [id] }];
        if (textingNumbers.length < 1) {
            const textWarning = type === 'text_template' || type === 'workflow' || type === 'conversation';
            // console.log(textingthings);
            if (textWarning) {
                setErrorMessage("You do not have any text compliant numbers, so you cannot add to a workflow or guided flow")
                numberWarning = true;
            }
        }
        else {
            numbers = textingNumbers;
        }
        if (!numberWarning || numbers !== '') {
            if (list.length < 100) {
                try {
                    const requestOptions = {
                        method: 'POST',
                        body: JSON.stringify({
                            leads: list,
                            actions: action,
                            agent: agentInfo.agentId,
                            numbers: textingNumbers
                        })
                    };
                    const response = await fetchWithDefaults('/bulkOptions', requestOptions);
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                    const jsonData = await response.json();
                    if (jsonData === true) {
                        const filterData = filterLeadsPayload(date_assigned, lead_age, state, last_contacted, never_contacted, amount_contacted, tags, lead_type, lead_level, lead_status, predefined_filter, tableOptions.row, tableOptions.size)

                        const { filterListOptions, filterRangeOptions, paginationOptions, sortOptions } = filterData
                        const leadResponse = await getLeadsByFilterOptions(filterListOptions, filterRangeOptions, paginationOptions, sortOptions, agentInfo.agentId, predefined_filter)

                        if (leadResponse) {
                            updateLeadData(leadResponse.data, leadResponse.meta)
                        }

                        setSuccessTrigger(true);
                        setShowBulkActionModal(false);
                        setSelectedLeadIds([]);
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            }
            else {
                setWarning({ length: list.length, action: action });
            }
        }
    }

    const sendBulk = async () => {
        closeAlert();
        try {
            const requestOptions = {
                method: 'POST',
                body: JSON.stringify({
                    leads: selectedLeadIds,
                    actions: warning.action,
                    agent: agentInfo.agentId,
                    numbers: textingNumbers
                })
            };
            const response = await fetchWithDefaults('/bulkOptions', requestOptions);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            await response.json();
            setSuccessTrigger(true);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    const closeAlert = () => {
        setShowBulkActionModal(false)
        setErrorMessage(false)
        setWarning(false)
    }

    const handleOpenLead = async (leadId) => {
        await dispatch(fetchLeadDataThunk({ leadId }))
        setLeadCard([leadsAlldata]);
    }

    const leadsAlldata = useSelector((state) => state.leadAllDataInfo?.leadsAllInfoData)
    const workflows = useSelector((state) => state.workflows.workflows);
    const status = useSelector((state) => state.status.status);
    const texts = useSelector((state) => state.texts.texts);
    const agentTags = useSelector((state) => state.tags.tags);

    const bulkActions = {
        TEXT: texts,
        TAG: agentTags,
        STATUS: status,
        WORKFLOW: workflows,
    }

    const updateBulkActions = (action, search) => {
        setModalActions({
            label: action,
            actions: bulkActions[action.toUpperCase()],
            search
        })

        setShowBulkActionModal(true)
    }

    const updateLeadData = (newLeadData, leadMetaData) => {
        setLeads(newLeadData)
        setMaxPage(leadMetaData?.last_page ?? 0)
        setTotalCount(leadMetaData?.total ?? 0)
        dispatch(setBackup(newLeadData))
        dispatch(setWholeQueue(newLeadData))
        setNoResults(newLeadData.length === 0)

        if (jump_into_queue && newLeadData.length > 0) {
            dispatch(toggleJumpIntoQueue(false))
            jumpIntoQueue()
        }
    }
	const getIntiial = (newLeadData, leadMetaData) => {
        setLeads(newLeadData)
        setMaxPage(leadMetaData?.last_page ?? 0)
        setTotalCount(leadMetaData?.total ?? 0)

        dispatch(setQueue(newLeadData))
        dispatch(setBackup(newLeadData))
        dispatch(setWholeQueue(newLeadData))

        // console.log('results', newLeadData.length, newLeadData.length === 0)
        setNoResults(newLeadData.length === 0)

        if (jump_into_queue && newLeadData.length > 0) {
            dispatch(toggleJumpIntoQueue(false))
            jumpIntoQueue()
        }
    }

    const [loading, setLoading] = useState(false)
    const [noResults, setNoResults] = useState(false)

    useEffect(() => {
        dispatch(setLoadingGif(loading))
    }, [loading])

    const getFreshLeads = async () => {
        const paginationOptions = {
            page: 1,
            numPerPage: tableOptions.size
        }
        const predefined_filter = {
            name: "Active & New",
            params: {
                type: "Active New",
                filterType : 'initialLoad'
            }
        }
        setLoading(true)

        try {
            const leadResponse = await getLeadsByFilterOptions([], [], paginationOptions, [], agentInfo.agentId, predefined_filter)
            dispatch(resetPredefinedFilter())
            updateLeadData(leadResponse.data ?? [], leadResponse.meta)
            if(update_table){
                dispatch(setTableUpdate(false))
            }
        } catch (error) {
            console.error(error)
        }

        setLoading(false)
    }

    const resetAllFilters = async () => {
        dispatch(removeAllFilters())
        await getFreshLeads()
    }

    useEffect(()=>{
        let idx = leads.findIndex( l => l.lead_id === leadsAlldata.lead_id)

        if(idx > -1){
            const udpatedLeads = [...leads]
            udpatedLeads[idx] = leadsAlldata
            setLeads(udpatedLeads)
        }
    } , [leadsAlldata])

	/**
	 * @param {PointerEvent} evt
	 * @param {number} index
	 */
	const tableHeaderCellClicked = async (evt, index) => {
		let sortOrder = SortOrder.Asc;
		if ((index === tableOptions.sortIndex) && (tableOptions.sortOrder === SortOrder.Asc)) {
			sortOrder = SortOrder.Desc;
		}
		setTableOptions(
			{
				...tableOptions,
				sortIndex: index,
				sortOrder,
			}
		)
	}
	return (
		<>
			{showBulkActionModal && (
				<div className="bulkSelectionHolder showList">
					<div className='bulkSelectionContent'>
						<div style={{ height: 30, width: 30, backgroundSize: '30px', cursor: 'pointer', marginLeft: '96%'}} className='x-close' onClick={_ => setShowBulkActionModal(false)}></div>
						<div className='subTitle'>{modalActions.label}</div>
						{modalActions.actions.map((value, choiceIndex) => (
							<div className='quilityDataLine' key={choiceIndex} style={{lineHeight: 'normal', textAlign: 'left', textIndent: '10px'}}>
								<div className='quilityDataLineTitle' style={{width: '60%'}}>{value.name}</div>
								<div className='quilityDataLineValue' style={{display: 'inline-block', fontWeight: 'normal', cursor: 'pointer'}} onClick={() => bulk(
									value.id,
									modalActions.search,
								)}>Select
								</div>
							</div>
						))}
					</div>
					{errorMessage && (
						<Warning title="Unable To Send Texts" message="You do not have any texting capable numbers registered to message from" buttons={[
							{
								name: "Okay",
								action: "confirm",
							},
						]} confirm={closeAlert}/>
					)}
					{warning && (
						<Warning title="Large Amount Of Leads" message={`Are you sure you want to send messages to this many leads? Doing this will text ${warning.length} people.`} buttons={[
							{
								name: "Cancel",
								action: "cancel",
							},
							{
								name: "I'm Sure",
								action: "confirm",
							},
						]} confirm={sendBulk} cancel={closeAlert}/>
					)}
				</div>
			)}
			<SuccessAlert message="Your bulk actions have been applied" successTrigger={successTrigger} setSuccessTrigger={setSuccessTrigger}/>
			{save_filters && (
				<div className="bulkSelectionHolder showList">
					<SaveCurrentLeadTableFilter/>
				</div>
			)}
			<div style={{alignSelf: "stretch", justifyContent: "flex-start", alignItems: "flex-start", gap: 20, display: "flex", flexDirection: "column"}}>
				<div style={{paddingTop: 20, paddingLeft: 24, paddingRight: 24, justifyContent: "flex-start", gap: 16, display: "flex"}}>
					<div>
						<PrimaryButton
							buttonLabel="Add Filter"
							leftIconConfigs={{
								icon: "math-plus-q",
								rotate: true,
								color: "white",
							}}
							clickHandler={_ => setTimeout(
								() => setShowFilterList(!showFilterList),
								250,
							)}
							style={{padding: "5px 16px", backgroundColor: showFilterList ? "#005851" : undefined}}
						/>
						{showFilterList && (
							<FilterSelector
								closeHandler={() => {
									if (showFilterList === true) {
                                        setTimeout(() => { setShowFilterList(false) }, 250)
									}
								}}
							/>
						)}
					</div>
					{activeFilterCount > 0 && (
						<>
                            <OutlineButton
                                buttonLabel="Save Filter"
                                clickHandler={_ => dispatch(captureCurrentFilters(true))}
                                disabled={loading}
                                style={{padding: "5px 16px"}}
                            />
						</>
					)}
					<PrimaryButton
						buttonLabel='Apply Filter'
						clickHandler={_ =>activeFilterCount > 0 ? getLeads() : getInitialLeads()}
						disabled={loading || (!pending_filters && !(excludeAll && activeFilterCount > 0))}
						style={{padding: "5px 16px"}}
					/>
				</div>
				{(activeFilterCount > 0 || predefined_filter.name) && (
					<>
						<div style={{width: "100%", height: "100%", paddingTop: 10, paddingBottom: 20, paddingLeft: 14, paddingRight: 24, flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: 10, display: "inline-flex"}}>
							<div style={{height: 20, flexDirection: "column", justifyContent: "center", alignItems: "flex-start", display: "flex"}}>
								<div style={{justifyContent: "flex-start", alignItems: "center", gap: 4, display: "inline-flex"}}>
								<input
									className="qCheckBox"
									type="checkbox"
									onChange={e => {
										setExcludeAll(e.target.checked);
										dispatch(updateAllFilterExclusion({exclude: e.target.checked}));
									}}
									ref={excludeAllRef}
									disabled={loading || (selectedFilters.length < 1)}
									style={{cursor : selectedFilters.length < 1 ? 'default' : 'pointer'}}
								 />
									<div onClick={selectedFilters.length < 1 ? undefined : _ => dispatch(updateAllFilterExclusion({exclude: !excludeAllRef.current.checked}))} style={{opacity: (selectedFilters.length < 1) ? .5 : 1, cursor: (selectedFilters.length < 1) ? "default" : "pointer", textBoxTrim: "trim-both", textBoxEdge: "cap alphabetic", color: "var(--Colors-Foreground-fg-primary-(900), #2F2F2F)", fontSize: 16, fontFamily: "Montserrat", fontWeight: "400", wordWrap: "break-word"}}>
										Exclude all applied filters
									</div>
								</div>
							</div>
						</div>
						<div style={{maxWidth: "785px", height: "100%", paddingLeft: 24, paddingRight: 24, justifyContent: "flex-start", alignItems: "flex-end", gap: 16, display: "inline-flex", flexWrap: "wrap", alignContent: "flex-start"}}>
							{predefined_filter.name && (
								<SelectedPredefinedFilterInfo/>
							)}
							{selectedFilters.map((f, index) => (
								<>
									{f.type === leadFilterTypes.LIST
										? (
											<SelectedListFilterInfo
												key={`Selected_List_Filter_${index}`}
												filter={f.filter}
												title={f.title}
												filterName={f.filterName}
											/>
										)
										: f.type === leadFilterTypes.RANGE
											? (
												<SelectedRangeFilterInfo
													key={`Selected_Range_Filter_${index}`}
													filter={f.filter}
													title={f.title}
													filterName={f.filterName}
												/>
											)
											: (
												null
											)}
								</>
							))}
							{selectedFilters.length > 0 && (
								<div style={{overflow: "hidden", justifyContent: "center", alignItems: "center", gap: 8, display: "flex"}}>
									<UnderlineButton
										buttonLabel="Clear All"
										clickHandler={_ => {
                                            dispatch(removeAllFilters())
                                            dispatch(updatePendingFilters(true)) // show apply filter if all filters have been cleared out
                                        }}
										disabled={loading}
									/>
								</div>
							)}
						</div>
					</>
				)}
			</div>
			<div style={{height: 600, width: "-webkit-fill-available", padding: 24, position: "relative", flexDirection: "column", alignItems: "flex-start", gap: 22, display: "flex", alignSelf: "stretch"}}>
				<div style={{width: "100%", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", gap: 20, display: "flex"}}>
					<div style={{height: "90%", alignSelf: "stretch", maxHeight: "91%", paddingBottom: 10, background: "var(--Colors-Background-bg-primary, white)", overflow: "hidden", borderRadius: 6, flexDirection: "column", justifyContent: "flex-start", alignItems: "center", display: "flex"}}>
						{
							noResults
								? (
									<div style={{alignSelf: "stretch", height: 585, paddingBottom: 10, justifyContent: "flex-start", alignItems: "flex-start", display: "inline-flex"}}>
										<div style={{width: "100%", height: "100%", paddingLeft: 24, paddingRight: 24, paddingTop: 40, paddingBottom: 40, background: "var(--Colors-Background-bg-active, #F8F8F8)", borderBottom: "1px var(--Colors-Border-border-secondary, #F1F1F1) solid", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 40, display: "inline-flex"}}>
											<div style={{alignSelf: "stretch", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 48, display: "flex"}}>
												<div style={{width: 192, height: 160, paddingLeft: 32, paddingRight: 32, overflow: "hidden", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 8, display: "flex"}}>
													<div className="maginifying-glass-gears" style={{height: "100%", width: "85%", backgroundSize: "162px"}}></div>
												</div>
												<div style={{width: "100%", maxWidth: 560, flexDirection: "column", justifyContent: "flex-start", alignItems: "center", gap: 24, display: "flex"}}>
													<div style={{alignSelf: "stretch", textBoxTrim: "trim-both", textBoxEdge: "cap alphabetic", textAlign: "center", color: "var(--Colors-Foreground-fg-primary-(900), #2F2F2F)", fontSize: 36, fontFamily: "Montserrat", fontWeight: "700", wordWrap: "break-word"}}>No entries match your filters
													</div>
													<div style={{alignSelf: "stretch", textBoxTrim: "trim-both", textBoxEdge: "cap alphabetic", textAlign: "center", color: "var(--Colors-Foreground-fg-tertiary-(600), #8D8D8D)", fontSize: 16, fontFamily: "Montserrat", fontWeight: "400", wordWrap: "break-word"}}>Trying removing some or all filters.
													</div>
												</div>
												<PrimaryButton
													buttonLabel="Reset All Filters"
													clickHandler={_ => resetAllFilters()}
													disabled={loading}
												/>
											</div>
										</div>
									</div>
								)
								: (
									<div className="fun-filterable-lead-table fun-filterable-lead-table-full-width">
										<div className="fun-filterable-lead-table-table-container">
											<table className="fun-filterable-lead-table-table">
												<thead>
													<tr className="fun-filterable-lead-table-header-row fun-filterable-lead-table-sticky-header">
														<th className="fun-filterable-lead-table-header-cell">
															<input
																className="qCheckBox"
																name="select_all"
																onChange={e => e.target.checked ? setSelectedLeadIds(leads.map(l => l.lead_id)) : setSelectedLeadIds([])}
																checked={selectedLeadIds.length === leads.length && leads.length > 0}
																type="checkbox"
															/>
														</th>
														{
															tableColumns.map(
																(tblCol, idx) => {
																	return (
																		<th className="fun-filterable-lead-table-header-cell fun-sortable" key={idx} onClick={evt => tableHeaderCellClicked(evt, idx)}>
																			<span className="fun-sort-wrap">
																				{tblCol.label}
																				<SortOrderIndicator
																					isVisible={tableOptions.sortIndex === idx}
																					sortOrder={tableOptions.sortOrder}
																				/>
																			</span>
																		</th>
																	)
																}
															)
														}
													</tr>
												</thead>
												<tbody className="fun-filterable-lead-table-content">
													{
														leads.map(
															(obj, idx) => {
																return (
																	<tr className="fun-filterable-lead-table-row" key={idx}>
																		<td className="fun-filterable-lead-table-cell">
																			<input
																				checked={selectedLeadIds.includes(obj.lead_id)}
																				className="qCheckBox"
																				onChange={evt => updateSelectedleads(obj.lead_id, evt.target.checked)}
																				type="checkbox"
																			/>
																		</td>
																		<td className="fun-filterable-lead-table-cell" onClick={() => handleOpenLead(obj.lead_id)}>
																			{obj.borrower_last}, {obj.borrower_first}
																		</td>
																		<td className="fun-filterable-lead-table-cell" onClick={() => handleOpenLead(obj.lead_id)}>
																			{obj.state}
																		</td>
																		<td className="fun-filterable-lead-table-cell" onClick={() => handleOpenLead(obj.lead_id)}>
																			{
																				(obj.last_contacted
																					? formatDate(obj.last_contacted)
																					: undefined) || "Never Contacted"
																			}
																		</td>
																		<td className="fun-filterable-lead-table-cell" onClick={() => handleOpenLead(obj.lead_id)}>
																			{formatDate(obj.date_assigned)}
																		</td>
																		<td className="fun-filterable-lead-table-cell" onClick={() => handleOpenLead(obj.lead_id)}>
																			{obj.amount_contacted}
																		</td>
																	</tr>
																);
															},
														)
													}
												</tbody>
											</table>
										</div>
										{/* Pagination */}
										<div style={{paddingLeft: 20, paddingRight: 20, paddingTop: 12, paddingBottom: 12, borderTop: "1px solid #F1F1F1", justifyContent: "flex-end", alignItems: "center", gap: 36, display: "inline-flex"}}>
											<div style={{width: "100%", background: "white", justifyContent: "flex-end", alignItems: "center", gap: 36, display: "inline-flex"}}>
												<div style={{justifyContent: "flex-start", alignItems: "center", gap: 12, display: "flex"}}>
													<div style={{color: "black", fontSize: 12, fontFamily: "Montserrat", fontWeight: "400", letterSpacing: 0.06, wordWrap: "break-word"}}>Rows per page:
													</div>
													<div style={{overflow: "hidden", justifyContent: "center", alignItems: "center", gap: 4, display: "flex"}}>
														<Dropdown
															options={pageOptions}
															changeHandler={e => updateTableSize(parseInt(e.target.value))}
															inputValue={tableOptions.size}
															disabled={loading || noResults}
														/>
													</div>
												</div>
												<div style={{color: "black", fontSize: 13, fontFamily: "Montserrat", fontWeight: "500", letterSpacing: 0.06, wordWrap: "break-word"}}>{tableOptions.start + 1} – {Math.min(
													tableOptions.end,
													totalCount,
												)} of {totalCount}</div>
												<div style={{justifyContent: "flex-start", alignItems: "flex-start", gap: 12, display: "flex", marginRight: "20px"}}>
													<div onClick={_ => (loading || noResults)
														? null
														: getPrevRow()} style={{padding: 10, background: (tableOptions.row > 1) ? "#46C3B2" : "#D0D0D0", borderRadius: 100, overflow: "hidden", justifyContent: "center", alignItems: "center", gap: 8, display: "flex", cursor: "pointer"}}>
														<div style={{width: 18, height: 18, position: "relative"}}>
															<div style={{width: 6, height: 10, position: "absolute"}}>
																<Icon name="q-chevron-left" height={18} size={18} adjust="fontello" color={(tableOptions.row > 1)
																	? "#46C3B2"
																	: "#D0D0D0"} viewBox="0 0 16 16" strokeOptions={{
																	stroke: "white",
																	width: "2",
																	lineCap: "round",
																	lineJoin: "round",
																}}/>
															</div>
														</div>
													</div>
													<div onClick={_ => (loading || noResults)
														? null
														: getNextRow()} style={{padding: 10, background: (tableOptions.row < maxPage) ? "#46C3B2" : "#D0D0D0", borderRadius: 100, overflow: "hidden", justifyContent: "center", alignItems: "center", gap: 8, display: "flex", cursor: "pointer"}}>
														<div style={{width: 18, height: 18, position: "relative"}}>
															<div style={{width: 6, height: 10, position: "absolute"}}>
																<Icon name="q-chevron-right" height={18} size={18} adjust="fontello" color={(tableOptions.row < maxPage)
																	? "#46C3B2"
																	: "#D0D0D0"} viewBox="0 0 16 16" strokeOptions={{
																	stroke: "white",
																	width: "2",
																	lineCap: "round",
																	lineJoin: "round",
																}}/>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								)
						}
					</div>
					{/* Dial/Bulk action buttons */}
					<div style={{width: "95.5%", justifyContent: "space-between", alignItems: "flex-end", gap: 15, display: "flex", flexWrap: "nowrap", gap: 10 , marginTop : '-10px'}}>
						<div style={{width: "calc(20% - 30px)"}}>
							<PrimaryButton
								buttonLabel="Dial"
								clickHandler={_ => dailSelected()}
								buttonClass={loading || noResults || selectedLeadIds.length === 0
									? "q-bulk-action-dial-disabled"
									: "q-bulk-action-dial"}
								disabled={loading || noResults || selectedLeadIds.length === 0}
							/>
						</div>
						<div style={{width: "calc(20% - 30px)"}}>
							<OutlineButton
								buttonLabel="Text"
								clickHandler={_ => updateBulkActions(
									"Text",
									"text_template",
								)}
								buttonClass={loading || noResults || selectedLeadIds.length === 0
									? "q-bulk-action-button-disabled"
									: "q-bulk-action-button"}
								disabled={loading || noResults || selectedLeadIds.length === 0}
							/>
						</div>
						<div style={{width: "calc(20% - 30px)"}}>
							<OutlineButton
								buttonLabel="Tag"
								clickHandler={_ => updateBulkActions(
									"Tag",
									"tags",
								)}
								buttonClass={loading || noResults || selectedLeadIds.length === 0
									? "q-bulk-action-button-disabled"
									: "q-bulk-action-button"}
								disabled={loading || noResults || selectedLeadIds.length === 0}
							/>
						</div>
						<div style={{width: "calc(20% - 30px)"}}>
							<OutlineButton
								buttonLabel="Status"
								clickHandler={_ => updateBulkActions(
									"Status",
									"lead_status",
								)}
								buttonClass={loading || noResults || selectedLeadIds.length === 0
									? "q-bulk-action-button-disabled"
									: "q-bulk-action-button"}
								disabled={loading || noResults || selectedLeadIds.length === 0}
							/>
						</div>
						<div style={{width: "calc(20% - 30px)"}}>
							<OutlineButton
								buttonLabel="Workflow"
								clickHandler={_ => updateBulkActions(
									"Workflow",
									"workflow",
								)}
								buttonClass={loading || noResults || selectedLeadIds.length === 0
									? "q-bulk-action-button-disabled"
									: "q-bulk-action-button"}
								disabled={loading || noResults || selectedLeadIds.length === 0}
							/>
						</div>
					</div>
				</div>
			</div>
			{loading && (
				<div className="loadingScreen">
					<div className="loaderWrapper">
						<img src={image} alt="Loading Gif" style={{width: "100%"}}/>
					</div>
				</div>
			)}
		</>
	);
}

function SortOrderIndicator(props) {
	const {
		sortOrder,
		isVisible,
		...rest
	} = props;
	let iconName = "q-down";
	if (sortOrder === SortOrder.Desc) {
		iconName = "q-up";
	}
	let className = "fun-sort-wrap-icon";
	if (isVisible) {
		className = `${className} fun-is-visible`;
	}
	return (
		<span className={className}>
			<Icon name={iconName} size={18} {...rest}/>
		</span>
	)
}

/**
 * @param {string} isoFormattedDate
 * @returns {string}
 */
function formatDate(isoFormattedDate) {
	return moment.utc(isoFormattedDate).format('MM/DD/YYYY')
}
export default LeadTable;